import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PdfViewer, type HighlightTarget } from '../../components/ui/PdfViewer';
import { JsonDiffViewer } from '../../components/benchmark/JsonDiffViewer';
import { groundTruthService, schemaService } from '../../services';
import { formatLlmInstructions } from '../../utils/formatLlmInstructions';
import { GroundTruthDocument, FieldDefinitions, GtBenchmarkRunOption, GtBenchmarkResultDetail, ExtractionSchema } from '../../types';

export const GroundTruthEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [document, setDocument] = useState<GroundTruthDocument | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingReviewed, setMarkingReviewed] = useState(false);

  const [fieldDefs, setFieldDefs] = useState<FieldDefinitions | null>(null);
  const [activeSchema, setActiveSchema] = useState<ExtractionSchema | null>(null);
  const [fieldDefsLoading, setFieldDefsLoading] = useState(true);
  const [gtValues, setGtValues] = useState<Record<string, unknown>>({});
  // #579 — Mode brouillon : les modifs restent locales jusqu'au "Sauvegarder N modifications"
  const [draftValues, setDraftValues] = useState<Record<string, unknown>>({});
  const [savingAll, setSavingAll] = useState(false);
  const modifiedFields = useMemo(() => new Set(Object.keys(draftValues)), [draftValues]);
  const hasUnsavedChanges = modifiedFields.size > 0;

  const currentPdfUrlRef = useRef<string>('');

  // Benchmark comparison state
  const [benchmarkRuns, setBenchmarkRuns] = useState<GtBenchmarkRunOption[] | null>(null);
  const [benchmarkRunsLoading, setBenchmarkRunsLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [benchmarkResult, setBenchmarkResult] = useState<GtBenchmarkResultDetail | null>(null);

  // Provenance state (PDF navigation)
  const [highlight, setHighlight] = useState<HighlightTarget | undefined>();
  const [benchmarkProvenance, setBenchmarkProvenance] = useState<
    Record<string, { snippet: string; page: number; bbox?: number[] }>
  >({});
  // PROD1.9 — confiance par champ de l'analyse sélectionnée (pastilles #645)
  const [benchmarkConfidences, setBenchmarkConfidences] = useState<
    Record<string, import('../../types/extraction').FieldConfidence>
  >({});

  // Resizable divider (2-column: PDF | Right panel)
  const [leftWidthPercent, setLeftWidthPercent] = useState(40);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // PROD1.7.5 — Field deletion modal state
  const [deleteFieldModal, setDeleteFieldModal] = useState<{
    fieldKey: string;
    totalCount: number;
    scope: 'this_doc' | 'all_docs';
  } | null>(null);
  const [deleteFieldSubmitting, setDeleteFieldSubmitting] = useState(false);

  // Load document data
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);

    groundTruthService
      .get(id)
      .then((doc) => {
        setDocument(doc);
        setGtValues(doc.expected_extraction || {});
      })
      .catch((err) => setError(err.message || 'Erreur lors du chargement du document'))
      .finally(() => setLoading(false));
  }, [id]);

  // Load field definitions from active schema
  useEffect(() => {
    if (!document) return;

    setFieldDefsLoading(true);
    schemaService
      .getActive(document.domain)
      .then((schema) => {
        setActiveSchema(schema);
        if (schema.field_definitions) {
          setFieldDefs(schema.field_definitions);
        }
      })
      .catch((err) => {
        console.error('Failed to load schema field definitions:', err);
      })
      .finally(() => setFieldDefsLoading(false));
  }, [document?.domain]);

  // Load PDF with proper cleanup
  useEffect(() => {
    if (!id) return;

    let isCancelled = false;

    if (currentPdfUrlRef.current) {
      URL.revokeObjectURL(currentPdfUrlRef.current);
      currentPdfUrlRef.current = '';
    }
    setPdfUrl('');

    groundTruthService
      .getPdfUrl(Number(id))
      .then((url) => {
        if (!isCancelled) {
          currentPdfUrlRef.current = url;
          setPdfUrl(url);
        } else {
          URL.revokeObjectURL(url);
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          console.error('Failed to load GT PDF:', err);
        }
      });

    return () => {
      isCancelled = true;
      if (currentPdfUrlRef.current) {
        URL.revokeObjectURL(currentPdfUrlRef.current);
        currentPdfUrlRef.current = '';
      }
    };
  }, [id]);

  // Load benchmark runs list
  const handleLoadRuns = async () => {
    if (!id) return;

    setBenchmarkRunsLoading(true);
    try {
      const runs = await groundTruthService.listBenchmarkResults(id);
      setBenchmarkRuns(runs);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur chargement benchmarks: ${message}`);
    } finally {
      setBenchmarkRunsLoading(false);
    }
  };

  // Select a benchmark run
  const handleSelectRun = async (runId: string) => {
    if (!id) return;

    setSelectedRunId(runId);

    try {
      const result = await groundTruthService.getBenchmarkResult(id, runId);
      setBenchmarkResult(result);
      setBenchmarkProvenance(result.provenance ?? {});
      setBenchmarkConfidences(result.field_confidences ?? {});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur chargement résultat: ${message}`);
      setSelectedRunId(null);
      setBenchmarkResult(null);
    }
  };

  // Close benchmark comparison
  const handleCloseBenchmark = () => {
    setBenchmarkResult(null);
    setSelectedRunId(null);
    setHighlight(undefined);
    setBenchmarkProvenance({});
    setBenchmarkConfidences({});
  };

  const handleProvenanceClick = (fieldPath: string) => {
    const prov = benchmarkProvenance[fieldPath];
    if (prov) {
      // La provenance benchmark stocke le bbox en tableau [x1,y1,x2,y2] ;
      // le viewer attend la forme objet {x1,y1,x2,y2}.
      const bbox =
        prov.bbox && prov.bbox.length === 4
          ? { x1: prov.bbox[0], y1: prov.bbox[1], x2: prov.bbox[2], y2: prov.bbox[3] }
          : null;
      setHighlight({ items: [{ page: prov.page, snippet: prov.snippet, bbox }] });
    }
  };

  const handleClearHighlight = () => {
    setHighlight(undefined);
  };

  const handleMarkReviewed = async () => {
    if (!document) return;

    setMarkingReviewed(true);
    try {
      await groundTruthService.updateFlags(Number(document.id), {
        needs_review: false,
      });
      setDocument({ ...document, needs_review: false });
      toast.success('Document marqué comme révisé');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(message);
    } finally {
      setMarkingReviewed(false);
    }
  };

  // PROD1.7.5 — Ouvre la modale de suppression d'un champ GT-only.
  // Pré-charge le count global pour permettre à l'utilisateur de voir l'ampleur
  // avant de choisir entre suppression locale et suppression globale.
  const handleFieldDelete = useCallback(async (fieldKey: string) => {
    try {
      const { count } = await groundTruthService.countFieldOccurrences(fieldKey);
      setDeleteFieldModal({ fieldKey, totalCount: count, scope: 'this_doc' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Impossible de compter les occurrences : ${message}`);
    }
  }, []);

  const confirmFieldDelete = useCallback(async () => {
    if (!deleteFieldModal || !document) return;
    const { fieldKey, totalCount, scope } = deleteFieldModal;
    setDeleteFieldSubmitting(true);
    try {
      if (scope === 'this_doc') {
        await groundTruthService.deleteField(Number(document.id), fieldKey);
        setGtValues((prev) => {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        });
        setDraftValues((prev) => {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        });
        toast.success(`Champ « ${fieldKey} » supprimé de ce document`);
      } else {
        const result = await groundTruthService.bulkDeleteField(fieldKey, totalCount);
        setGtValues((prev) => {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        });
        setDraftValues((prev) => {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        });
        toast.success(`Champ « ${fieldKey} » supprimé de ${result.deleted_from} GT`);
      }
      setDeleteFieldModal(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(message);
    } finally {
      setDeleteFieldSubmitting(false);
    }
  }, [deleteFieldModal, document]);

  // #579 — Pose la modif en draft local au lieu de PATCH immédiat.
  // Le commit groupé se fait via handleSaveAll (bouton "Sauvegarder N modifications").
  const handleFieldSave = useCallback(
    async (fieldKey: string, newValue: unknown) => {
      if (!document) return;
      setDraftValues((prev) => {
        // Si la valeur draft est identique à la valeur BD, retirer l'entrée draft.
        const original = gtValues[fieldKey] ?? null;
        const normalizedNew = newValue ?? null;
        if (original === normalizedNew || String(original) === String(normalizedNew)) {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        }
        return { ...prev, [fieldKey]: newValue };
      });
    },
    [document, gtValues]
  );

  const handleSaveAll = useCallback(async () => {
    if (!document || !hasUnsavedChanges || savingAll) return;
    setSavingAll(true);
    const entries = Object.entries(draftValues);
    let okCount = 0;
    const failed: string[] = [];
    for (const [fieldKey, newValue] of entries) {
      try {
        await groundTruthService.editField(Number(document.id), {
          field_path: fieldKey,
          new_value: newValue,
          reason: 'Correction manuelle',
        });
        okCount += 1;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erreur';
        failed.push(`${fieldKey}: ${message}`);
      }
    }
    if (okCount > 0) {
      setGtValues((prev) => {
        const next = { ...prev };
        for (const [k, v] of entries) {
          if (!failed.some((f) => f.startsWith(`${k}:`))) next[k] = v;
        }
        return next;
      });
      setDraftValues((prev) => {
        const next = { ...prev };
        for (const [k] of entries) {
          if (!failed.some((f) => f.startsWith(`${k}:`))) delete next[k];
        }
        return next;
      });
    }
    if (failed.length === 0) {
      toast.success(`${okCount} modification${okCount > 1 ? 's' : ''} sauvegardée${okCount > 1 ? 's' : ''}`);
    } else {
      toast.error(`${okCount} sauvegardées, ${failed.length} échouée(s) : ${failed.join(' | ')}`);
    }
    setSavingAll(false);
  }, [document, draftValues, hasUnsavedChanges, savingAll]);

  const handleCancelAll = useCallback(() => {
    if (!hasUnsavedChanges) return;
    if (window.confirm(`Annuler ${modifiedFields.size} modification(s) non sauvegardée(s) ?`)) {
      setDraftValues({});
    }
  }, [hasUnsavedChanges, modifiedFields.size]);

  // #579 — Guard navigation : avertir si modifs non sauvegardées
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const ok = window.confirm(
        `${modifiedFields.size} modification(s) non sauvegardée(s). Quitter sans sauvegarder ?`
      );
      if (ok) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker, modifiedFields.size]);

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    window.document.body.style.userSelect = 'none';
    window.document.body.style.cursor = 'col-resize';

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const percent = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setLeftWidthPercent(Math.min(70, Math.max(20, percent)));
    };

    const onMouseUp = () => {
      setDragging(false);
      window.document.body.style.userSelect = '';
      window.document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  // Unwrap field_results: the backend stores {field_results: {...}, weighted_scoring: {...}}
  // The actual field diffs are nested inside field_results.field_results
  const actualFieldResults = useMemo(() => {
    if (!benchmarkResult) return null;
    const fr = benchmarkResult.field_results as any;
    // If there's a nested field_results key, use that; otherwise use the top level
    if (fr && typeof fr === 'object' && 'field_results' in fr && typeof fr.field_results === 'object') {
      return fr.field_results as Record<string, any>;
    }
    return fr as Record<string, any>;
  }, [benchmarkResult]);

  // Build enriched diff: all schema fields + extra benchmark fields
  const enrichedDiff = useMemo(() => {
    if (!fieldDefs) return [];

    const items: Array<{
      field: string;
      expected: any;
      actual: any;
      expectedAtRunTime?: any;
      status: 'match' | 'mismatch' | 'missing' | 'extra' | 'both_null';
      severity: number;
      error_type?: string;
      weight?: number;
      in_schema?: boolean;
      source: 'schema' | 'out_of_schema' | 'gt_only';
    }> = [];

    // Helper : valeur GT effective (draft si modifié, sinon BD)
    const effectiveExpected = (key: string): unknown => {
      if (key in draftValues) return draftValues[key];
      return gtValues[key] ?? null;
    };

    // Toute info "côté gauche" (nom + badges + boite GT) vient du schéma
    // actif ou du GT en BD. Jamais du run benchmark sélectionné. Le run ne
    // remplit que la colonne droite (actual) et les marqueurs de provenance.
    //
    // All fields from the active schema. Le poids vient du schéma
    // (`scoring.weight` du field_definitions) — propriété intrinsèque du
    // champ, indépendante du run.
    for (const fieldKey of Object.keys(fieldDefs.fields)) {
      const fieldDef = fieldDefs.fields[fieldKey];
      const schemaWeight = fieldDef?.scoring?.weight;
      const benchmarkField = actualFieldResults?.[fieldKey];
      if (benchmarkField) {
        items.push({
          field: fieldKey,
          expected: effectiveExpected(fieldKey),
          actual: benchmarkField.actual,
          expectedAtRunTime: benchmarkField.expected,
          status: benchmarkField.status,
          severity: benchmarkField.severity,
          error_type: benchmarkField.error_type,
          weight: schemaWeight,
          in_schema: true,
          source: 'schema',
        });
      } else {
        items.push({
          field: fieldKey,
          expected: effectiveExpected(fieldKey),
          actual: undefined,
          status: 'both_null',
          severity: 0,
          weight: schemaWeight,
          in_schema: true,
          source: 'schema',
        });
      }
    }

    // Champs présents dans le run mais ABSENTS du schéma actif (source =
    // 'out_of_schema'). Cas typique : le run a été fait avec une version
    // antérieure du schéma qui définissait ces champs, mais la config
    // courante ne les inclut plus. Ils tombent dans la section « Autres ».
    //
    // À NE PAS CONFONDRE avec `status: 'extra'` (sémantique du comparateur :
    // "actual a une valeur, expected est null") qui s'applique à n'importe
    // quel champ schéma ou non.
    //
    // Pas de poids ici : le champ n'est pas dans le schéma actif, donc pas
    // de scoring.weight défini. La valeur expected reste celle du GT
    // (effectiveExpected) si la clé existe dans expected_extraction.
    if (actualFieldResults) {
      for (const [key, result] of Object.entries(actualFieldResults)) {
        if (!(key in fieldDefs.fields)) {
          const r = result as any;
          items.push({
            field: key,
            expected: effectiveExpected(key),
            actual: r.actual,
            expectedAtRunTime: r.expected,
            status: r.status,
            severity: r.severity,
            error_type: r.error_type,
            in_schema: false,
            source: 'out_of_schema',
          });
        }
      }
    }

    // #576 — Champs GT non couverts par le schéma actif ni par le run benchmark
    const allGtKeys = new Set([...Object.keys(gtValues), ...Object.keys(draftValues)]);
    for (const key of allGtKeys) {
      if (key in fieldDefs.fields) continue;
      if (actualFieldResults && key in actualFieldResults) continue;
      items.push({
        field: key,
        expected: effectiveExpected(key),
        actual: undefined,
        status: 'missing',
        severity: 0,
        in_schema: false,
        source: 'gt_only',
      });
    }

    return items;
  }, [fieldDefs, actualFieldResults, gtValues, draftValues]);

  // Build section mappings for JsonDiffViewer
  const fieldSectionsMap = useMemo(() => {
    if (!fieldDefs) return undefined;
    const mapping: Record<string, string> = {};
    for (const [fieldKey, fieldDef] of Object.entries(fieldDefs.fields)) {
      mapping[fieldKey] = fieldDef.ui.section;
    }
    // #576 — Route GT-only items to the dedicated virtual section.
    // Benchmark extras (source: 'out_of_schema') intentionally stay unmapped
    // so JsonDiffViewer falls them back into "Autres".
    for (const item of enrichedDiff) {
      if (item.source === 'gt_only') {
        mapping[item.field] = '_gt_only';
      }
    }
    return mapping;
  }, [fieldDefs, enrichedDiff]);

  const fieldInstructionsMap = useMemo(() => {
    if (!fieldDefs) return undefined;
    const mapping: Record<string, string> = {};
    for (const [fieldKey, fieldDef] of Object.entries(fieldDefs.fields)) {
      const text = formatLlmInstructions(fieldDef.llm);
      if (text) mapping[fieldKey] = text;
    }
    return Object.keys(mapping).length > 0 ? mapping : undefined;
  }, [fieldDefs]);

  const sectionsMap = useMemo(() => {
    if (!fieldDefs) return undefined;
    const result: Record<string, { label_fr: string; order: number; icon?: string }> = {};
    for (const [key, config] of Object.entries(fieldDefs.sections)) {
      result[key] = { label_fr: config.label_fr, order: config.order, icon: config.icon };
    }
    // #576 — Section virtuelle pour les champs GT non couverts par le schéma actif.
    result['_gt_only'] = {
      label_fr: 'Champs GT hors schéma actif',
      order: 9999,
      icon: undefined,
    };
    return result;
  }, [fieldDefs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">Erreur : {error}</p>
          <button
            onClick={() => navigate('/benchmark/ground-truth')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-600">Document non trouvé</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/benchmark/ground-truth')}
            className="text-gray-600 hover:text-gray-800"
          >
            &larr; Retour
          </button>
          <div>
            <p className="text-sm text-gray-500">
              <span
                className="hover:underline cursor-pointer"
                onClick={() => navigate('/benchmark/ground-truth')}
              >
                Ground Truth
              </span>
              {' \u2192 '}
              <span>{document.filename}</span>
            </p>
            <div className="flex items-center gap-3 mt-1">
              <h1 className="text-xl font-semibold text-gray-900">
                {document.filename}
              </h1>
              {document.needs_review && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  needs_review
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* #579 — Indicateur + boutons globaux du mode brouillon */}
          {hasUnsavedChanges && (
            <>
              <span className="text-sm text-amber-700 font-medium">
                {modifiedFields.size} modification{modifiedFields.size > 1 ? 's' : ''} non sauvegardée{modifiedFields.size > 1 ? 's' : ''}
              </span>
              <button
                onClick={handleCancelAll}
                disabled={savingAll}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                Annuler tout
              </button>
              <button
                onClick={handleSaveAll}
                disabled={savingAll}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                {savingAll ? 'Sauvegarde...' : `Sauvegarder ${modifiedFields.size} modification${modifiedFields.size > 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {document.needs_review && (
            <button
              onClick={handleMarkReviewed}
              disabled={markingReviewed}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
            >
              {markingReviewed ? 'En cours...' : 'Marquer comme révisé'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content — 2 columns: PDF | Enhanced Diff Viewer */}
      <div ref={containerRef} className="flex flex-1 overflow-hidden">
        {/* PDF Viewer — Left */}
        <div
          className="bg-gray-50 overflow-hidden flex flex-col"
          style={{ width: `${leftWidthPercent}%`, pointerEvents: dragging ? 'none' : undefined }}
        >
          {pdfUrl ? (
            <PdfViewer url={pdfUrl} filename={document.filename} highlight={highlight} onClearHighlight={handleClearHighlight} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Chargement du PDF...</p>
            </div>
          )}
        </div>

        {/* Resizable divider */}
        <div
          onMouseDown={handleDividerMouseDown}
          className="w-1.5 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 transition-colors"
        />

        {/* Right panel — JsonDiffViewer enrichi */}
        <div className="bg-white overflow-auto" style={{ width: `${100 - leftWidthPercent}%`, pointerEvents: dragging ? 'none' : undefined }}>
          <div className="p-6">
            {/* #575 — Indicateur du schéma utilisé pour construire les sections */}
            {activeSchema && (() => {
              const selectedRun = benchmarkRuns?.find((r) => r.run_id === selectedRunId);
              const runSchemaVersion = selectedRun?.schema_version;
              const versionMismatch =
                !!runSchemaVersion && runSchemaVersion !== activeSchema.version;
              return (
                <div className="mb-4 text-xs text-gray-600 flex items-center flex-wrap gap-2">
                  <span>
                    Schéma : <span className="font-medium text-gray-800">{activeSchema.name}</span>{' '}
                    v{activeSchema.version}
                  </span>
                  {versionMismatch && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 italic">
                      Run exécuté avec v{runSchemaVersion}
                    </span>
                  )}
                </div>
              );
            })()}
            {fieldDefsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                <p className="text-gray-500 text-sm">Chargement des champs...</p>
              </div>
            ) : enrichedDiff.length === 0 ? (
              <p className="text-gray-500 text-center py-12">
                Aucun champ disponible dans le schéma actif.
              </p>
            ) : (
              <JsonDiffViewer
                diff={enrichedDiff}
                fieldInstructions={fieldInstructionsMap}
                fieldSections={fieldSectionsMap}
                sections={sectionsMap}
                editable={true}
                documentFilename={document.filename}
                onFieldSave={handleFieldSave}
                onFieldDelete={handleFieldDelete}
                modifiedFields={modifiedFields}
                provenance={benchmarkProvenance}
                confidences={benchmarkConfidences}
                onProvenanceClick={handleProvenanceClick}
                benchmarkRuns={benchmarkRuns}
                benchmarkRunsLoading={benchmarkRunsLoading}
                selectedRunId={selectedRunId}
                onSelectRun={handleSelectRun}
                onLoadRuns={handleLoadRuns}
                onCloseBenchmark={handleCloseBenchmark}
              />
            )}
          </div>
        </div>
      </div>

      {/* PROD1.7.5 — Modale de suppression de champ GT-only */}
      {deleteFieldModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => !deleteFieldSubmitting && setDeleteFieldModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-3">
              Supprimer le champ <span className="font-mono text-red-600">{deleteFieldModal.fieldKey}</span>
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Ce champ est présent dans <strong>{deleteFieldModal.totalCount}</strong>{' '}
              document{deleteFieldModal.totalCount > 1 ? 's' : ''} GT au total.
            </p>
            <div className="space-y-2 mb-6">
              <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="delete-scope"
                  value="this_doc"
                  checked={deleteFieldModal.scope === 'this_doc'}
                  onChange={() => setDeleteFieldModal({ ...deleteFieldModal, scope: 'this_doc' })}
                  className="mt-1"
                />
                <span className="text-sm">
                  <strong>Seulement de ce document</strong>
                  <span className="block text-gray-500">Les autres GT conservent ce champ.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-red-50 border-red-200">
                <input
                  type="radio"
                  name="delete-scope"
                  value="all_docs"
                  checked={deleteFieldModal.scope === 'all_docs'}
                  onChange={() => setDeleteFieldModal({ ...deleteFieldModal, scope: 'all_docs' })}
                  className="mt-1"
                />
                <span className="text-sm">
                  <strong className="text-red-700">
                    De tous les GT ({deleteFieldModal.totalCount} document{deleteFieldModal.totalCount > 1 ? 's' : ''})
                  </strong>
                  <span className="block text-gray-500">
                    Action irréversible. Utiliser pour nettoyer un champ créé par erreur lors d'un import.
                  </span>
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteFieldModal(null)}
                disabled={deleteFieldSubmitting}
                className="px-4 py-2 text-sm border rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmFieldDelete}
                disabled={deleteFieldSubmitting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {deleteFieldSubmitting ? 'Suppression...' : 'Confirmer la suppression'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
